import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ScopingItemsSharedService } from '@app/cem/project/scoping-items/scoping-items-shared-service';
import { SplitterModule } from '@progress/kendo-angular-layout';
import { Subscription } from 'rxjs';
import { DependentItemsComponent } from '../components/dependent-items/dependent-items.component';
import { ScopingItemsPadComponent } from '../components/scoping-items-pad/scoping-items-pad.component';
import { ScopingItemsComponent } from '../components/scoping-items/scoping-items.component';
import { DependentItem } from '../models/dependent-item';
import { DragDropItemComponentType, DragDropItemEventDto } from '../models/drag-drop-item.models';
import { IItemScratchPadChangeEventType } from '../models/iitem-scratch-pad-change-event-type';
import { ScopingItemGroupMapping } from '../models/scoping-item-group-mapping';
import { ItemManagementBrokerService } from '../services/item-management-broker.service';

@Component({
  selector: 'app-scoping-items-index',
  templateUrl: './scoping-items-index.component.html',
  styleUrls: ['./scoping-items-index.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    DependentItemsComponent,
    ScopingItemsComponent,
    ScopingItemsPadComponent,
    SplitterModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    ItemManagementBrokerService
  ]
})
export class ScopingItemsIndexComponent implements OnInit, OnDestroy {
  public scopingItemsSharedService = inject(ScopingItemsSharedService);
  private broker = inject(ItemManagementBrokerService);

  private dragDropSubscription: Subscription;

  ngOnInit() {
    this.scopingItemsSharedService.loadLookups();
    this.dragDropSubscription = this.broker.DragDropEvent.subscribe((s: DragDropItemEventDto) => {
      this.handleDragDropEvent(s);
    });
  }

  ngOnDestroy() {
    if (this.dragDropSubscription) {
      this.dragDropSubscription.unsubscribe();
    }
  }

  private handleDragDropEvent(dragDropEventDto: DragDropItemEventDto): void {
    if (!dragDropEventDto) {
      return;
    }

    switch (dragDropEventDto.componentType) {
      case DragDropItemComponentType.ScopingItem: {
        this.handleScopingItemDragDropEvent(dragDropEventDto);
        break;
      }
      case DragDropItemComponentType.DependentItem: {
        this.handleDependentItemDragDropEvent(dragDropEventDto);
        break;
      }
    }
  }

  private handleScopingItemDragDropEvent(dragDropEventDto: DragDropItemEventDto) {
    // This handles the drop event of scoping item to the ScratchPad.
    if (dragDropEventDto.event.data.source === 'ScopingItemsGrid') {
      this.addScopingItemToPadFromGrid(dragDropEventDto.event.data);
    }
  }

  private addScopingItemToPadFromGrid(data: any) {
    let group = this.broker.latestScopingItemGroupMapping.value.find(f => f.ScopingItemId === data.ItemId);

    let newPad: ScopingItemGroupMapping = ScopingItemGroupMapping.Create(group, this.broker.RecordChangedEvent.value);

    this.broker.ScratchPadChangeEvent.next({ data: newPad, type: IItemScratchPadChangeEventType.Created });
  }

  private handleDependentItemDragDropEvent(dragDropEventDto: DragDropItemEventDto) {
    // This handles the drop event of dependent item to the scoping item.
    if (dragDropEventDto.event.data.source === 'DependentItemsGrid') {
      this.addDependentItemToPadFromGrid(dragDropEventDto.event.data, dragDropEventDto.ScopingItemId);
    }
  }

  private addDependentItemToPadFromGrid(data: any, scopingItemId: number) {
    let dependentItem = new DependentItem();
    dependentItem.DependentItemId = data.ItemId;
    dependentItem.DependentItemName = data.Name;
    dependentItem.DependentItemNumber = data.ItemNumber;
    dependentItem.ProjectSubTypeId = data.ProjectSubTypeId;
    dependentItem.Quantity = data.Quantity;
    dependentItem.IsNew = true;

    this.broker.AddDependentItemEvent.next({ dependentItem: dependentItem, destScopingItemId: scopingItemId });
  }
}